'use client';

import type { ReactNode } from 'react';
import { useState, useEffect, useCallback } from 'react';
import { Ruler } from 'lucide-react';

interface Dimension {
  id: string;
  label: string;
  value: string;
  unit: string;
  axis: 'x' | 'y' | 'z';
  position: { top: string; left: string };
}

interface Props {
  productMetadata?: Record<string, any>;
  /**
   * When set, the floating ruler button is omitted and you receive the same toggle node
   * to place beside the viewer toolbar (shared row, no overlap).
   */
  toolbarCluster?: (rulerToggle: ReactNode) => ReactNode;
  /** Hide X/Y/Z axis legend chip (bottom-left of viewer). */
  hideAxisLegend?: boolean;
}

const DEFAULT_DIMENSIONS: Dimension[] = [
  { id: 'width', label: 'Width', value: '—', unit: 'cm', axis: 'x', position: { top: '85%', left: '50%' } },
  { id: 'height', label: 'Height', value: '—', unit: 'cm', axis: 'y', position: { top: '50%', left: '8%' } },
  { id: 'depth', label: 'Depth', value: '—', unit: 'cm', axis: 'z', position: { top: '75%', left: '80%' } },
];

export function DimensionOverlay({ productMetadata, toolbarCluster, hideAxisLegend = false }: Props) {
  const [visible, setVisible] = useState(false);
  const [dimensions, setDimensions] = useState<Dimension[]>([]);

  useEffect(() => {
    const metaDims = productMetadata?.dimensions;
    if (metaDims && Array.isArray(metaDims)) {
      setDimensions(
        metaDims.map((d: any, i: number) => ({
          id: d.id || `dim-${i}`,
          label: d.label || `Dim ${i + 1}`,
          value: String(d.value || '—'),
          unit: d.unit || 'cm',
          axis: d.axis || 'x',
          position: d.position || DEFAULT_DIMENSIONS[i % DEFAULT_DIMENSIONS.length].position,
        })),
      );
    } else if (metaDims && typeof metaDims === 'object') {
      const entries = Object.entries(metaDims);
      setDimensions(
        entries.map(([key, val]: [string, any], i) => ({
          id: key,
          label: key.charAt(0).toUpperCase() + key.slice(1),
          value: String(val),
          unit: productMetadata?.dimensionUnit || 'cm',
          axis: (['x', 'y', 'z'] as const)[i % 3],
          position: DEFAULT_DIMENSIONS[i % DEFAULT_DIMENSIONS.length].position,
        })),
      );
    } else {
      setDimensions(DEFAULT_DIMENSIONS);
    }
  }, [productMetadata]);

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'd' && e.ctrlKey && e.shiftKey) {
      e.preventDefault();
      setVisible((v) => !v);
    }
  }, []);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  const axisColor: Record<string, string> = {
    x: 'border-red-400 text-red-500 bg-red-50/90 dark:bg-red-950/80',
    y: 'border-green-400 text-green-600 bg-green-50/90 dark:bg-green-950/80',
    z: 'border-blue-400 text-blue-500 bg-blue-50/90 dark:bg-blue-950/80',
  };

  const lineColor: Record<string, string> = {
    x: '#ef4444',
    y: '#22c55e',
    z: '#3b82f6',
  };

  const floatingToggleClass = visible
    ? 'bg-blue-500 text-white border-blue-400'
    : 'bg-white/80 dark:bg-zinc-800/80 border-zinc-200 dark:border-zinc-700 text-zinc-600 dark:text-zinc-300 hover:bg-white dark:hover:bg-zinc-800';

  const dockedToggleClass = visible
    ? 'bg-blue-500 text-white border-blue-400'
    : 'border-zinc-900/15 bg-gradient-to-b from-white/92 via-white/88 to-white/92 text-zinc-700 hover:bg-zinc-900/[0.06] hover:text-zinc-900 dark:border-white/15 dark:from-zinc-900/92 dark:via-zinc-900/88 dark:to-zinc-900/92 dark:text-zinc-200 dark:hover:bg-white/10 dark:hover:text-white';

  const toggleButton = (
    <button
      type="button"
      onClick={() => setVisible((v) => !v)}
      className={`z-20 shrink-0 flex items-center justify-center transition-colors backdrop-blur-md border shadow-md shadow-black/10 dark:shadow-black/40 ${
        toolbarCluster
          ? `h-8 w-8 rounded-full ${dockedToggleClass}`
          : `absolute top-3 p-2 rounded-md backdrop-blur-sm ${floatingToggleClass}`
      }`}
      style={toolbarCluster ? undefined : { right: '180px' }}
      title="Toggle dimensions (Ctrl+Shift+D)"
    >
      <Ruler className="h-4 w-4" />
    </button>
  );

  return (
    <>
      {toolbarCluster ? toolbarCluster(toggleButton) : toggleButton}

      {visible && (
        <div className="absolute inset-0 z-10 pointer-events-none">
          <svg className="absolute inset-0 w-full h-full" style={{ pointerEvents: 'none' }}>
            {dimensions.map((dim) => {
              const top = parseFloat(dim.position.top) / 100;
              const left = parseFloat(dim.position.left) / 100;
              const color = lineColor[dim.axis] || '#94a3b8';

              if (dim.axis === 'x') {
                const y = top * 100;
                return (
                  <g key={dim.id}>
                    <line x1="15%" y1={`${y}%`} x2="85%" y2={`${y}%`} stroke={color} strokeWidth="1.5" strokeDasharray="6 3" opacity="0.6" />
                    <line x1="15%" y1={`${y - 1.5}%`} x2="15%" y2={`${y + 1.5}%`} stroke={color} strokeWidth="2" opacity="0.8" />
                    <line x1="85%" y1={`${y - 1.5}%`} x2="85%" y2={`${y + 1.5}%`} stroke={color} strokeWidth="2" opacity="0.8" />
                  </g>
                );
              }
              if (dim.axis === 'y') {
                const x = left * 100;
                return (
                  <g key={dim.id}>
                    <line x1={`${x}%`} y1="15%" x2={`${x}%`} y2="85%" stroke={color} strokeWidth="1.5" strokeDasharray="6 3" opacity="0.6" />
                    <line x1={`${x - 1}%`} y1="15%" x2={`${x + 1}%`} y2="15%" stroke={color} strokeWidth="2" opacity="0.8" />
                    <line x1={`${x - 1}%`} y1="85%" x2={`${x + 1}%`} y2="85%" stroke={color} strokeWidth="2" opacity="0.8" />
                  </g>
                );
              }
              return null;
            })}
          </svg>

          {dimensions.map((dim) => (
            <div
              key={dim.id}
              className={`absolute -translate-x-1/2 -translate-y-1/2 pointer-events-auto px-2 py-1 rounded-md border text-[11px] font-medium backdrop-blur-sm whitespace-nowrap shadow-sm ${
                axisColor[dim.axis] || 'border-zinc-300 text-zinc-600 bg-white/90'
              }`}
              style={{ top: dim.position.top, left: dim.position.left }}
            >
              <span className="opacity-60 mr-1">{dim.label}</span>
              <span className="font-bold">{dim.value}</span>
              <span className="opacity-50 ml-0.5">{dim.unit}</span>
            </div>
          ))}

          {!hideAxisLegend && (
          <div className="absolute bottom-3 left-3 pointer-events-auto flex items-center gap-3 bg-white/90 dark:bg-zinc-800/90 backdrop-blur-sm rounded-md px-3 py-1.5 border border-zinc-200 dark:border-zinc-700 text-[10px] text-zinc-500">
            <span className="flex items-center gap-1"><span className="w-3 h-0.5 bg-red-400 inline-block rounded" /> X-axis</span>
            <span className="flex items-center gap-1"><span className="w-3 h-0.5 bg-green-400 inline-block rounded" /> Y-axis</span>
            <span className="flex items-center gap-1"><span className="w-3 h-0.5 bg-blue-400 inline-block rounded" /> Z-axis</span>
          </div>
          )}
        </div>
      )}
    </>
  );
}
